import { createSignal, onMount, onCleanup, Accessor } from 'solid-js';

// Unique identifier for this dropdown instance to link Portal content
let dropdownIdCounter = 0;

export const useDropdown = (): {
  isOpen: Accessor<boolean>;
  toggle(): void;
  close(): void;
  ref(el: HTMLDivElement): HTMLDivElement;
  dropdownId: string;
} => {
  const [isOpen, setIsOpen] = createSignal(false);
  let ref: HTMLDivElement | undefined;
  const dropdownId = `dropdown-${++dropdownIdCounter}`;

  const handleClickOutside = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    // Check if click is inside the ref element
    if (ref && ref.contains(target)) {
      return;
    }

    // Check if click is inside Portal content linked to this dropdown
    // Portal content should have data-dropdown-id attribute matching this dropdown
    const portalContent = target.closest(`[data-dropdown-id="${dropdownId}"]`);
    if (portalContent) {
      return;
    }

    setIsOpen(false);
  };

  onMount(() => {
    // Use 'mousedown' and 'touchstart' instead of 'click' for outside-click detection:
    // When using 'click', the event fires after the toggle button updates state and the DOM.
    // This can cause `ref.contains(event.target)` to be inconsistent, since the dropdown
    // children may or may not be mounted yet. 'mousedown' fires earlier, ensuring the
    // ref points to the correct, stable DOM when checking for outside clicks.
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('touchstart', handleClickOutside);
  });

  const toggle = (): void => {
    setIsOpen(!isOpen());
  };
  const close = (): void => {
    setIsOpen(false);
  };

  return {
    isOpen,
    toggle,
    close,
    ref: (el: HTMLDivElement) => (ref = el),
    dropdownId,
  };
};
